// src/LiveOutboxNotice.tsx
// ─────────────────────────────────────────────────────────────────────────────
//  Upozornění na neuložené výsledky rozhodčího (docs/live-faze1-navrh.md, 3.6)
//
//  Výsledky ze schránky „k odeslání“ (utils/liveOutbox), které po odeslání
//  fronty zápisů na serveru nejsou nebo tam je jiná hodnota (i oprava
//  hlavního rozhodčího) – zápis server zamítl po obnovení stránky, kdy to SDK
//  nijak neohlásí, nebo když hláška u výsledku už není vidět (závod ukončen,
//  rozhodčí odešel z rundy či obrazovky; terče hned). Rozhodčí je nahlásí
//  hlavnímu rozhodčímu a potvrdí „Rozumím“; znovu uložený výsledek zmizí sám.
//  Jiná hodnota na serveru nemusí znamenat zamítnutí: zápis potvrzený až po
//  obnovení stránky (schránka o potvrzení neví), který pak hlavní rozhodčí
//  opravil, vypadá stejně – text proto zamítnutí netvrdí.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import type { LostEntry } from "./utils/liveOutbox";
import { fmtPoints } from "./utils/formatPoints";

interface Props {
  entries: LostEntry[];
  /** Popisek stanoviště podle id výsledku (např. „3. Gongy“). */
  stationLabel: (stationId: string) => string;
  /** „Rozumím“ – rodič záznamy ze schránky odstraní. */
  onAcknowledge: () => void;
  /**
   * „Odeslat znovu“ u výsledku, který na serveru není (fáze 3, bod 5.4 –
   * záznam s ranami; rodič rozhodne, u kterého jde). Bez funkce tlačítko není.
   */
  onResend?: (entry: LostEntry) => void;
  canResend?: (entry: LostEntry) => boolean;
  /** Pás přes celou šířku pod horní lištou (bez zaoblení). */
  band?: boolean;
  style?: React.CSSProperties;
}

const LiveOutboxNotice: React.FC<Props> = ({ entries, stationLabel, onAcknowledge, onResend, canResend, band = false, style }) => {
  if (entries.length === 0) return null;
  return (
    // Nadpis, vysvětlení a „Rozumím“ zůstanou vidět vždy; při dlouhém
    // seznamu se posouvá jen seznam (na 360×640 dřív „Rozumím“ zmizelo)
    <div
      className={`alert alert-warning text-start small mb-0 py-2 px-3${band ? " rounded-0 border-0" : ""}`}
      role="alert"
      style={{ flexShrink: 0, maxHeight: "50vh", display: "flex", flexDirection: "column", ...style }}
    >
      <div className="fw-bold mb-1" style={{ flexShrink: 0 }}>
        <i className="bi bi-exclamation-triangle-fill me-1" />
        Tyto výsledky se neuložily nebo se liší od serveru
      </div>
      <div className="mb-1" style={{ flexShrink: 0 }}>
        Zápis nebyl povolen (závod je ukončený, výsledek opravil hlavní rozhodčí, nebo nejste v seznamu
        rozhodčích), nebo výsledek mezitím změnil hlavní rozhodčí. Nahlaste je hlavnímu rozhodčímu.
      </div>
      <ul className="mb-2 ps-3" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
        {entries.map((e) => (
          <li key={`${e.startNumber}_${e.stationId}_${e.at}`}>
            #{e.startNumber} stanoviště {stationLabel(e.stationId)}: zadáno {fmtPoints(e.score)} b.,{" "}
            {e.serverScore === null
              ? "na serveru –"
              : `na serveru je jiná hodnota ${fmtPoints(e.serverScore)} b. (zápis byl zamítnut, nebo výsledek změnil hlavní rozhodčí)`}
            {onResend && (!canResend || canResend(e)) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-dark px-2 ms-2"
                style={{ minHeight: "2.75rem" }}
                onClick={() => onResend(e)}
              >
                Odeslat znovu
              </button>
            )}
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn-sm btn-warning align-self-start" style={{ flexShrink: 0 }} onClick={onAcknowledge}>
        Rozumím
      </button>
    </div>
  );
};

export default LiveOutboxNotice;
